/**
 * Wire + view-model types for the xplatform mapping service job APIs.
 * Wire types mirror `xplatform_mapping_service/docs/APIs.md`.
 * ViewModel types are the camelCase shapes consumed by the React UI.
 * Convert at the boundary (mappingApi / notificationApi) using the
 * builders under `src/utils/builders/`.
 */

export const JobStatus = {
  AwaitingUpload: "AwaitingUpload",
  Parsing: "Parsing",
  Running: "Running",
  Completed: "Completed",
  CompletedWithErrors: "CompletedWithErrors",
  Failed: "Failed",
} as const;

export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

export type JobInputKind = "text" | "json" | "csv" | "excel" | "zip";
export type JobSourceKind = "single" | "zip" | "batch";

// ---------- Wire types (snake_case, server-shaped) ---------------------

export interface CreateJobRequest {
  service: string;
  plugin_id: string;
  input_kind: JobInputKind;
  filename: string;
  /** Job-wide overrides; ZIP entries are matched by their full member path. */
  options?: Record<string, unknown>;
  files?: { name: string; options: Record<string, unknown> }[];
}

export interface CreateJobResponse {
  job_id: string;
  status: JobStatus;
  upload_url: string;
  upload_content_type: string;
  upload_expires_at: string;
  max_bytes: number;
}

export interface JobError {
  error_code: string;
  message: string;
  description?: string;
}

/** One input file, irrespective of upload, ZIP membership, or extraction origin. */
export interface JobFile {
  file_index: number;
  status: "Pending" | "Running" | "Completed" | "Failed";
  input_kind: JobInputKind;
  display_name: string;
  member_path?: string | null;
  origin?: { kind: "upload" | "zip_member" | "extraction_process_item"; ref: string } | null;
  has_input_pdf: boolean;
  error?: JobError | null;
  source_units?: { kind: string; total: number; done: number } | null;
  rows_total: number;
  rows_ok: number;
  rows_failed: number;
  rows_no_match: number;
  accepted_count: number;
}

export interface JobFilesResponse {
  files: JobFile[];
}

export interface JobStatusResponse {
  job_id: string;
  service: string;
  process_id?: string | null;
  plugin_id: string;
  status: JobStatus;
  input_kind: JobSourceKind;
  filename: string;
  file_count: number;
  files_done: number;
  failed_files: number;
  rows_total: number | null;
  rows_ok: number;
  rows_failed: number;
  rows_no_match: number;
  accepted_count: number;
  error: JobError | null;
  created_at: string;
  updated_at: string;
}

export interface OriginalFileResponse {
  download_url: string;
  filename: string;
}

/** Creator identity retained after completion; email is not persisted. */
export interface JobOwner {
  user_id: string | null;
  first_name: string | null;
  last_name: string | null;
}

export interface JobListItem {
  job_id: string;
  plugin_id: string;
  service: string;
  status: JobStatus;
  input_kind: JobSourceKind;
  filename: string;
  file_count: number;
  files_done: number;
  failed_files: number;
  rows_total: number | null;
  rows_ok: number;
  rows_failed: number;
  rows_no_match: number;
  error: JobError | null;
  created_by: JobOwner | null;
  created_at: string;
  updated_at: string;
}

export interface JobListResponse {
  jobs: JobListItem[];
  cursor: string | null;
}

/** Terminal job event — `write_result` publishes one per job. */
export interface MappingJobTerminalWebSocketMessage {
  type: "mapping.job.completed" | "mapping.job.failed";
  job_id: string;
  plugin_id: string;
  status: JobStatus;
  result_url_path: string;
}

/** Per-file processing progress while a job is running. */
export interface MappingJobProgressWebSocketMessage {
  type: "mapping.job.progress";
  job_id: string;
  status: JobStatus;
  file_index?: number;
  source_units?: { kind: string; total: number; done: number };
  rows_total: number;
  rows_ok: number;
  rows_failed?: number;
}

export type MappingJobWebSocketMessage =
  | MappingJobTerminalWebSocketMessage
  | MappingJobProgressWebSocketMessage;

/**
 * Per-plugin review-UI renderer instructions.
 *
 * Backend resolves the active plugin config and attaches the
 * ``display`` block to every /jobs/{id}/result page so the FE renders
 * plugin-specific headlines, breadcrumbs, and expanded-row columns
 * without a second fetch. ``null`` (or missing fields) → FE falls
 * back to its convention-based renderer.
 */
export type AltColumnRender = "text" | "id" | "monospace" | "score";

export interface AltColumn {
  metadata_key: string;
  label: string;
  width_pct: number;
  render: AltColumnRender;
}

export interface DisplayConfig {
  headline_fields: string[];
  headline_separator: string;
  identity_field: string | null;
  breadcrumb_fields: string[];
  alt_columns: AltColumn[];
}

// ---------- Catalogue item requests (WD `no_match` rows) -------------

/**
 * One missing catalogue entry, as the item-request mail presents it.
 *
 * The four `oe_*` fields are the OEM source component in the customer's own
 * words — the same values the .xlsx export's first four columns carry, so a
 * maintainer can match the mail against the exported sheet. They mirror
 * `api_service/models/response/wd/item_requests.py`.
 */
export interface ItemRequestLine {
  row_index: number;
  oe_number: string | null;
  oe_name: string | null;
  oe_function: string | null;
  oe_domain: string | null;
  /** What the select LLM proposes the new entry be called. An AI suggestion,
   *  NOT a stored record — empty when nothing was retrieved for the row. */
  suggested_item_mp_text: string | null;
}

/** Body of `POST /jobs/{job_id}/item-requests/preview` — all fields optional. */
export interface ItemRequestPreviewRequest {
  /** Rows to include; omit for every row of the job still awaiting an entry.
   *  Named rows that are not misses are ignored, so the review table's current
   *  selection can be posted unfiltered. */
  row_indexes?: number[];
  /** Recipients; omit for the deployment's configured maintainer list. Must
   *  sit in the deployment's allowed domains — a 400 otherwise. */
  to?: string[];
  note?: string;
}

/**
 * The mail, before anyone sends it. Carries the message twice on purpose:
 * `subject` / `body` are what will be sent (and what the reviewer edits),
 * `lines` is the same content structured so a table can be rendered.
 */
export interface ItemRequestPreviewResponse {
  job_id: string;
  to: string[];
  subject: string;
  body: string;
  lines: ItemRequestLine[];
  row_count: number;
  /** The rows this mail covers. Echo it back as the send call's `row_indexes`
   *  so the sent message and its reported `row_count` describe the same set. */
  row_indexes: number[];
  /** Rows that matched before the 200-row per-request cap. */
  total_row_count: number;
  /** True when the cap bit: this mail covers only the first `row_count` of
   *  them, and the rest need a second request. */
  truncated: boolean;
  /** Name of the .xlsx the mail carries. The rows are NOT in `body` — the
   *  recipient creates catalogue entries, which is spreadsheet work; `lines`
   *  is the same data structured, for rendering the table on screen. */
  attachment_filename: string;
}

/** Body of `POST /jobs/{job_id}/item-requests`. */
export interface SendItemRequestRequest {
  row_indexes?: number[];
  to?: string[];
  cc?: string[];
  /** The reviewer's edited COVERING NOTE: sent VERBATIM. The rows are not in
   *  here — the attachment is always rebuilt from `row_indexes`, so editing
   *  this cannot change what data goes out. Omit both to have the server build
   *  the message the preview would have. */
  subject?: string;
  body?: string;
  /** Only used when the server builds the body. */
  note?: string;
}

export interface SendItemRequestResponse {
  job_id: string;
  message_id: string;
  to: string[];
  cc: string[];
  subject: string;
  row_count: number;
  row_indexes: number[];
  total_row_count: number;
  truncated: boolean;
  attachment_filename: string;
}

/** Body of `GET /jobs/{job_id}/result/export`. The server builds the .xlsx,
 *  uploads it, and returns a short-lived presigned download URL.
 *  `download_url` is null when the job has no result rows. */
export interface ResultExportResponse {
  download_url: string | null;
  filename: string;
  row_count: number;
}

// ---------- ViewModel types (camelCase, UI-shaped) ---------------------

export interface JobErrorViewModel {
  errorCode: string;
  message: string;
  description?: string;
}

/** Owner identity (camelCase, UI-shaped). */
export interface JobOwnerViewModel {
  userId: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface JobFileViewModel {
  fileIndex: number;
  status: JobFile["status"];
  inputKind: JobInputKind;
  filename: string;
  memberPath: string | null;
  hasInputPdf: boolean;
  error: JobErrorViewModel | null;
  sourceUnits: JobFile["source_units"];
  rowsTotal: number;
  rowsOk: number;
  rowsFailed: number;
  rowsNoMatch: number;
  acceptedCount: number;
}

export interface JobViewModel {
  jobId: string;
  pluginId: string;
  processId?: string | null;
  files?: JobFileViewModel[];
  service?: string;
  status: JobStatus;
  inputKind: JobSourceKind;
  fileCount: number;
  filesDone: number;
  failedFiles: number;
  filename: string;
  rowsTotal: number | null;
  rowsOk: number;
  rowsFailed: number;
  rowsNoMatch: number;
  acceptedCount: number;
  error: JobErrorViewModel | null;
  owner?: JobOwnerViewModel | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Owner + date-range filter for the org-wide job list. Status/service/
 * plugin scoping is passed separately (they already exist on the page).
 * `userId` is what the backend filters on; `userEmail` is kept only so
 * the FilterPanel can render the selected user without a re-lookup.
 */
export interface JobListFilterViewModel {
  userId: string | null;
  userEmail: string | null;
  startDate: string | null;
  endDate: string | null;
  /** Job status to scope to; `"all"` means no status filter. */
  status: JobStatus | "all";
}

export interface CreateJobResponseViewModel {
  jobId: string;
  status: JobStatus;
  uploadUrl: string;
  uploadContentType: string;
  uploadExpiresAt: string;
  maxBytes: number;
}

export interface ResultExportViewModel {
  downloadUrl: string | null;
  filename: string;
  rowCount: number;
}

export interface JobListViewModel {
  jobs: JobViewModel[];
  cursor: string | null;
}

/** Terminal notification — the page re-fetches the job + results on this. */
export interface MappingJobTerminalNotification {
  type: "mapping.job.completed" | "mapping.job.failed";
  jobId: string;
  pluginId: string;
  status: JobStatus;
  resultUrlPath: string;
}

/** Progress notification keeps file-level source units separate from result rows. */
export interface MappingJobProgressNotification {
  type: "mapping.job.progress";
  jobId: string;
  status: JobStatus;
  fileIndex?: number;
  sourceUnits?: { kind: string; total: number; done: number };
  rowsOk: number;
  rowsFailed: number;
  rowsTotal: number;
}

export type MappingJobNotification =
  | MappingJobTerminalNotification
  | MappingJobProgressNotification;

// ---------- Helpers ----------------------------------------------------

const EXTENSION_TO_INPUT_KIND: Readonly<Record<string, JobInputKind>> = {
  json: "json",
  csv: "csv",
  xlsx: "excel",
  txt: "text",
  zip: "zip",
};

export const detectInputKind = (filename: string): JobInputKind | null => {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_TO_INPUT_KIND[ext] ?? null;
};

/**
 * Default upload cap enforced server-side (see
 * `mapping_core.DEFAULT_MAX_INPUT_BYTES`). Mirrored here so the
 * frontend can reject oversize files before issuing POST /jobs.
 * `POST /jobs` returns the authoritative cap in `maxBytes`; we
 * still rely on that response for a server-truth second check.
 */
export const DEFAULT_MAX_INPUT_BYTES = 50 * 1024 * 1024;

/**
 * Per-row review state — what a REVIEWER did.
 *
 * `Failed` is a DISPLAY value only. The backend stopped writing it to
 * `review_status` (a row the pipeline could not map was never reviewed, so it
 * is `pending` there); the result-row builder folds
 * `mapping_status === "failed"` back into this field so the Status column,
 * its filter chip and the disabled row actions keep working off one value.
 * It is never sent back in a PATCH — the API type rejects it.
 */
export const ReviewStatus = {
  Pending: "pending",
  Accepted: "accepted",
  Rejected: "rejected",
  Corrected: "corrected",
  Failed: "failed",
} as const;

export type ReviewStatus = (typeof ReviewStatus)[keyof typeof ReviewStatus];

/**
 * What a REVIEWER may actually set — every status except `Failed`.
 *
 * `Failed` is a display value the client folds in from `mapping_status` so the
 * Status column reads one field; the API rejects it, because a row the
 * pipeline could not map was never reviewed. Deriving the send-side type by
 * subtraction is what makes that unsendable rather than merely documented: a
 * `RowEdit` carrying `Failed` no longer compiles, so the rule cannot be broken
 * by a new call site the way a runtime guard could be.
 */
export type ReviewAction = Exclude<ReviewStatus, typeof ReviewStatus.Failed>;

/**
 * `Omit` that keeps a union a union.
 *
 * The built-in `Omit<A | B, K>` collapses its argument into ONE object type
 * with the merged (and therefore all-optional) keys, which quietly throws away
 * the discrimination `RowEdit` exists to enforce: `Omit<RowEdit, "editedAt">`
 * accepts `{corrected}` with no rank, the exact shape the API rejects.
 * Distributing over the members preserves it.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

/** Machine verdict, independent of review state. */
export const MappingStatus = {
  Failed: "failed",
  NoMatch: "no_match",
  AutoAccepted: "auto_accepted",
  Matched: "matched",
} as const;

export type MappingStatus = (typeof MappingStatus)[keyof typeof MappingStatus];

/**
 * In-memory edit a reviewer has applied to a row, before the PATCH lands.
 *
 *  - accept what the row points at (`Accepted`, no rank needed — an accept
 *    without one SEALS the stored pick rather than reverting to the top)
 *  - replace it with another candidate (`Corrected`, `chosenRank` REQUIRED —
 *    a correction IS a pick, including picking rank 1 back to undo one)
 *  - reject everything (`Rejected`)
 *
 * The rank/status pairing is expressed as a union, not as optional fields on
 * one shape: `Corrected` without a rank is the combination the API rejects,
 * and there is no reason for it to be constructible here.
 */
/** A reviewer's verdict before it is timestamped — what callers pass in. */
export type RowEditInput = DistributiveOmit<RowEdit, "editedAt">;

export type RowEdit = {
  /** Edited source value to persist alongside the verdict (inline value
   *  edit). Independent of the verdict — it changes the row's INPUT, not
   *  which candidate it points at. */
  text?: string;
  editedAt: string;
} & (
  | { reviewStatus: typeof ReviewStatus.Corrected; chosenRank: number }
  | {
      reviewStatus: typeof ReviewStatus.Accepted;
      /** Optional: naming one re-seals a different candidate. */
      chosenRank?: number;
    }
  | {
      reviewStatus:
        | typeof ReviewStatus.Pending
        | typeof ReviewStatus.Rejected;
      chosenRank?: never;
    }
);

/** `GET /plugins/wd/input-template` — the empty workbook, as JSON.
 *
 *  Base64 rather than a binary body: passing an .xlsx through API Gateway
 *  untouched needs `binaryMediaTypes` configured, and nothing is stored to
 *  presign because the file is the same for every caller.
 *
 *  `columns` is the accepted header row. It ships so the UI can name the
 *  columns without keeping its own copy — which is how the result-table labels
 *  went stale against the fields they headed. */
export interface InputTemplateResponse {
  filename: string;
  columns: string[];
  content_base64: string;
}
